import {createSelector} from '@reduxjs/toolkit';
import {ConfigurationInterpreter} from '@viamedici-spc/configurator-ts';
import {conditional, conditionals} from './util/conditional';
import {getDimmingLevelSteps, getValueOfFeature, hasFeatureValue, isSatisfied, pimText} from './util/values';
import {meterToPx, mmToPx} from './util/meterToPx';
import {getArticleCode, getArticleCodeJoinedComponents, getLightengine1Code, getLightengine2Code} from './util/getArticleCode';
import {featureInSection} from './util/featureInSection';
import {getSectionConfig} from './sectionConfig';
import {lightProfileMap} from './lightProfile/lightProfiles';
import {regionCheck} from './util/regionCheck';
import {stringToAttributeId} from './components/spc/util/spcUtils';
import {getFamilyConfig} from './familyConfig';

import {selectConfigHasPersonalisation, selectConfigPersonalisation} from './slices/configurationSlice';
import {colourToHexValueMap} from './valueMaps/colourToHexValueMap';
import {generalAttributeOptions} from './generalAttributeOptions';

export const selectTranslations = state => state.productData.translations;
const selectConditionalVariantValues = state => state.productData.conditionalVariantValues;
export const selectProductData = state => state.productData.productData;
const selectVariantCodeTypeTemplate = state => state.productData.variantCodeTypeTemplate;
const selectStatusCurrentSection = state => state.status.currentSection;
const selectConfiguration = state => state.configuration?.data;


export const selectDebug = state => state.status.debug;
export const selectDebugShowAll = state => state.status.debugShowAll;
export const selectDebugAeto = state => state.status.debugAeto;
export const selectComponentView = state => state.status.componentView;

export const selectSectionConfigOld = state => {
	const featureModelId = state.configuration && state.configuration.data.projects ? state.configuration.data.projects[0].project.featureModelId : null;
	const familyId = state.status ? state.status.family : null;
	return getSectionConfig(familyId, featureModelId);
};
export const selectFamilyConfig = state => {
	const familyId = state.status?.family;
	return getFamilyConfig(familyId);
};

export const selectLanguage = state => state.status.lang;
export const selectRegion = state => state.status.region;
export const selectFamily = state => state.status?.family;

export const selectUserLoggedIn = state => state.userProfile.data.loggedIn;

// Light Profile Configuration
export const selectBaseProfile = state => state.lightProfile.data.project?.base;
export const selectBaseProfileIsSatisfied = state => state.lightProfile.data.project?.base !== null;
export const selectZone = state => state.lightProfile.data.project?.zone;
export const selectZoneIsSatisfied = state => state.lightProfile.data.project?.zone !== null;
export const selectLightProfileLocation = state => state.lightProfile.data.project?.location;
export const selectLightProfileLocationIsSatisfied = state => state.lightProfile.data.project?.location !== null;
export const selectLuminary = state => state.lightProfile.data.project?.luminary;
export const selectLuminaryIsSatisfied = state => state.lightProfile.data.project?.luminary !== null;
export const selectProjectName = state => state.lightProfile.data.project.projectName;
export const selectPicoPassword = state => state.lightProfile.data.project.picoPassword;
export const selectCentralPowerControl = state => state.lightProfile.data.project?.centralPowerControl;
export const selectCentralPowerControlIsSatisfied = state => state.lightProfile.data.project?.centralPowerControl !== null;

export const selectDimmLevel = state => state.configuration.data.projects[0].project.dimmLevelData;
export const selectImages = state => state.configuration.data.projects[0].project.images;

export const selectProductDatas = state => state.productData.productDatas;
export const selectRelux = state => state.productData.relux1;

export const selectPriceDataPerModels = state => state.price.priceData?.priceDataPerModels;

export const selectProductImagesUrl = state => state.status.productImagesUrl;

// SPC
export const selectSpcSessions = state => state.spc.sessions;
export const selectSpcSession = state => state.spc.sessions.default;

export const selectSpcConfiguration = state => state.spc?.spcs?.default?.configuration;
export const selectSpcConfigurations = state => Object.values(state.spc?.spcs).reduce((configurations, currentSpc) => {
	configurations[currentSpc.instance] = currentSpc.configuration;
	return configurations;
}, {});

export const selectSpcIsSatisfied = createSelector(
	[selectSpcConfiguration],
	(spcConfiguration) => (spcConfiguration ? spcConfiguration.isSatisfied : null)
);

export const selectSectionConfig = createSelector(
	[selectSpcConfiguration, selectFamily],
	(spcConfiguration, family) => {
		const mode = getValueOfFeature('shared::ContextV3::CX_Usage', spcConfiguration);
		const housingType = getValueOfFeature('shared::ProductType::HS_HousingType', spcConfiguration);
		console.log('selectSectionConfigNew', family, mode, housingType);
		return getSectionConfig(family, mode, housingType);
	}
);
export const selectIsSatisfied = selectSpcIsSatisfied;

export const selectSpcAttributes = createSelector(
	[selectSpcConfiguration],
	(spcConfiguration) => (spcConfiguration ?
		Array.from(spcConfiguration.attributes.values()).filter((attribute) => attribute.type !== 'Component')
		: null)
);
export const selectSpcComponents = createSelector(
	[selectSpcConfiguration],
	(spcConfiguration) => (spcConfiguration ?
		Array.from(spcConfiguration.attributes.values()).filter((attribute) => attribute.type === 'Component')
		: null)
);

export const selectFirstSection = createSelector(
	[selectSectionConfig],
	(sectionConfig) => {
		console.log('selectFirstSection', sectionConfig);
		return sectionConfig ? sectionConfig[0] : null;
	}
);

export const selectFinalized = state => state.configuration.data.finalized;

export const selectFilteredSpcAttributes = createSelector(
	[selectSpcAttributes, selectFirstSection, selectDebugShowAll, selectRegion, selectUserLoggedIn],
	(spcAttributes, section,  debugShowAll, region, loggedIn) => (spcAttributes ? spcAttributes
			.filter(spcAttribute => !generalAttributeOptions?.[spcAttribute.key.replace(/_[0-9]/, '')]?.hidden) // filter when configured to be hidden in section config
			.filter(spcAttribute => !(generalAttributeOptions?.[spcAttribute.key.replace(/_[0-9]/, '')]?.requireLogin ?? false) || loggedIn) // if login required filter for non logged in users
			.filter(spcAttribute => spcAttribute.type === 'Choice') // only ChoiceAttributes
			.filter(spcAttribute => Array.from(spcAttribute.values?.values()).some(v => !v.isPossibleDecisionStatesImmutable))
			//.filter(spcAttribute => !(spcAttribute.isSatisfied && spcAttribute.values?.size <= 1)) // filter when not satisfied and only one value available
			.sort((a, b) => generalAttributeOptions?.[a.key.replace(/_[0-9]/, '')]?.priority - generalAttributeOptions?.[b.key.replace(/_[0-9]/, '')]?.priority
			)
		: null)
);
export const selectUnFilteredSpcAttributes = createSelector(
	[selectSpcAttributes, selectFirstSection],
	(spcAttributes, section) => (spcAttributes ? spcAttributes
		.sort((a, b) =>
			generalAttributeOptions?.[a.key.replace(/_[0-9]/, '')]?.priority - generalAttributeOptions?.[b.key.replace(/_[0-9]/, '')]?.priority
		) : null)
);
export const selectBasicSpcAttributes = createSelector(
	[selectFilteredSpcAttributes, selectFirstSection],
	(filteredSpcAttributes, section) => (filteredSpcAttributes ? filteredSpcAttributes
		.filter(spcAttribute => generalAttributeOptions?.[spcAttribute.key.replace(/_[0-9]/, '')]?.attributePriority === 'basic') : null)
);
export const selectUnfilteredBasicSpcAttributes = createSelector(
	[selectUnFilteredSpcAttributes, selectFirstSection],
	(unfilteredSpcAttributes, section) => (unfilteredSpcAttributes ? unfilteredSpcAttributes
		.filter(spcAttribute => generalAttributeOptions?.[spcAttribute.key.replace(/_[0-9]/, '')]?.attributePriority === 'basic') : null)
);
export const selectBasicSpcAttributesSatisfactionPercentage = createSelector(
	[selectBasicSpcAttributes],
	(basicSpcAttributes) => {
		const basicSpcAttributesCount = basicSpcAttributes ? basicSpcAttributes.length : 0;
		const basicSpcAttributesSatisfiedCount = basicSpcAttributes ? basicSpcAttributes.filter(attribute => attribute.isSatisfied).length : 0;
		return basicSpcAttributes ? Math.round((basicSpcAttributesSatisfiedCount / basicSpcAttributesCount) * 100) : 0;
	}
);
export const selectBasicSpcAttributesSatisfied = createSelector(
	[selectBasicSpcAttributes, selectSpcConfiguration],
	(basicSpcAttributes) => (basicSpcAttributes ? basicSpcAttributes.every(attribute => attribute.isSatisfied) : false));

export const selectDetailSpcAttributes = createSelector(
	[selectFilteredSpcAttributes, selectFirstSection],
	(filteredSpcAttributes, section) => (filteredSpcAttributes ? filteredSpcAttributes
		.filter(spcAttribute => generalAttributeOptions?.[spcAttribute.key.replace(/_[0-9]/, '')]?.attributePriority !== 'basic') : null)
);
export const selectUnfilteredDetailSpcAttributes = createSelector(
	[selectUnFilteredSpcAttributes, selectFirstSection],
	(unfilteredSpcAttributes, section) => (unfilteredSpcAttributes ? unfilteredSpcAttributes
		.filter(spcAttribute => generalAttributeOptions?.[spcAttribute.key.replace(/_[0-9]/, '')]?.attributePriority !== 'basic') : null)
);

export const selectDetailSpcAttributesSatisfactionPercentage = createSelector(
	[selectDetailSpcAttributes],
	(detailSpcAttributes) => {
		const detailSpcAttributesCount = detailSpcAttributes ? detailSpcAttributes.length : 0;
		const detailSpcAttributesSatisfiedCount = detailSpcAttributes ? detailSpcAttributes.filter(attribute => attribute.isSatisfied).length : 0;
		return Math.round((detailSpcAttributesSatisfiedCount / detailSpcAttributesCount) * 100);
	}
);
export const selectDetailSpcAttributesSatisfied = createSelector(
	[selectDetailSpcAttributes, selectSpcConfiguration],
	(detailSpcAttributes) => (detailSpcAttributes ? detailSpcAttributes.every(attribute => attribute.isSatisfied) : false));

export const selectUnsatisfiedSpcAttributes = createSelector(
	[selectSpcAttributes, selectFirstSection, selectProductData],
	(spcAttributes, section, productData) => (spcAttributes && productData ? spcAttributes
		.filter(spcAttribute => !spcAttribute.isSatisfied) // filter when not satisfied and only one value available
		.sort((a, b) =>
			!generalAttributeOptions?.[a.key.replace(/_[0-9]/, '')]?.priority - !generalAttributeOptions?.[b.key.replace(/_[0-9]/, '')]?.priority
		) : null)
);

export const selectProfileId = createSelector(
	[selectBaseProfile, selectZone, selectCentralPowerControl],
	(base, zone, centralPowerControl) => {
		const lightProfile = lightProfileMap[base];

		if (lightProfile && zone in lightProfile && centralPowerControl in lightProfile[zone]) {
			return lightProfile[zone][centralPowerControl];
		}

		return null;
	}
);


function conditionalSelectorFactory(id, type = 'EditorialTable', side = 1) {
	return createSelector([selectSpcConfiguration, selectConditionalVariantValues],
		(spcConfiguration, conditionalVariantValues) =>
			conditional(spcConfiguration, conditionalVariantValues, id, type, side)
	);
}

function hasFeatureValueSelectorFactory(spcAttributeKey, valueId) {
	return createSelector([selectSpcConfiguration],
		(spcConfiguration) =>
			hasFeatureValue(spcAttributeKey, valueId, spcConfiguration) ||
			(valueId === 'None' && getValueOfFeature('root::Heads::Head_2:Type::Luminaire::LightEngine::LE_LightEngineModuleSize', spcConfiguration) === null)
	);
}

export function spcValueSelectorFactory(id, nofilter = false) {
	return createSelector(
		[selectSpcConfiguration, selectProductData, selectTranslations, selectFirstSection],
		(spcConfiguration, productData, translations, section) => {
			let values = [];
			const attributeId = stringToAttributeId(id);
			if (spcConfiguration) {
				const attribute = ConfigurationInterpreter.getChoiceAttribute(spcConfiguration, attributeId);
				if (attribute) {
					values = Array.from(attribute.values.values())
						.sort((a, b) =>
							!generalAttributeOptions?.[a.key.replace(/_[0-9]/, '')]?.priority - !generalAttributeOptions?.[b.key.replace(/_[0-9]/, '')]?.priority
						)
						.filter(value => nofilter || value.possibleDecisionStates.includes('Included'))
						.map(value => ({
								id: value.id,
								displayColour: colourToHexValueMap[value.id],
								label: {
									de: pimText('de', id, 'FV', spcConfiguration, translations, value.id),
									en: pimText('en', id, 'FV', spcConfiguration, translations, value.id),
									fr: pimText('fr', id, 'FV', spcConfiguration, translations, value.id)
								},
								info: {
									de: pimText('de', id, 'FV.L', spcConfiguration, translations, value.id),
									en: pimText('en', id, 'FV.L', spcConfiguration, translations, value.id),
									fr: pimText('fr', id, 'FV.L', spcConfiguration, translations, value.id)
								},
								description: {
									de: pimText('de', id, 'FV.L', spcConfiguration, translations, value.id),
									en: pimText('en', id, 'FV.L', spcConfiguration, translations, value.id),
									fr: pimText('fr', id, 'FV.L', spcConfiguration, translations, value.id)
								}
							}
						));
				} else {
					console.error('attribute not found with id', attributeId, spcConfiguration);
				}
			}
			return values;
		}
	);
}

export const selectCurrentSection = createSelector([selectSectionConfig, selectStatusCurrentSection],
	(sectionConfig, statusCurrentSection) => {
		const index = sectionConfig.findIndex(s => s.id === statusCurrentSection);
		if (statusCurrentSection && index !== -1) {
			return statusCurrentSection;
		} else {
			return sectionConfig[0].id;
		}
	}
);
export const selectCurrentSectionConfig = createSelector([selectSectionConfig, selectStatusCurrentSection],
	(sectionConfig, statusCurrentSection) => {
		const index = sectionConfig.findIndex(s => s.id === statusCurrentSection);
		return index !== -1 ? sectionConfig[index] : null;
	}
);

export const selectArticleCode = createSelector([selectProductData, selectSpcAttributes, selectSpcConfiguration],
	(productData, spcAttributes, spcConfiguration) => (
		getArticleCode(spcAttributes, spcConfiguration, productData)
	)
);
export const selectArticleCodeJoinedComponents = createSelector([selectProductData, selectSpcAttributes, selectSpcConfiguration],
	(productData, spcAttributes, spcConfiguration) => (
		getArticleCodeJoinedComponents(spcAttributes, spcConfiguration, productData)
	)
);
export const selectLightengine1Code = createSelector([selectProductData, selectSpcAttributes, selectSpcConfiguration],
	(productData, spcAttributes, spcConfiguration) => (
		getLightengine1Code(spcAttributes, spcConfiguration, productData)
	)
);
export const selectLightengine2Code = createSelector([selectProductData, selectSpcAttributes, selectSpcConfiguration],
	(productData, spcAttributes, spcConfiguration) => (
		getLightengine2Code(spcAttributes, spcConfiguration, productData)
	)
);
export const selectProductModelId = createSelector([selectConfiguration],
	(configuration) => (
		configuration.projects[0].project.productModelId
	)
);

export const selectDiameter = conditionalSelectorFactory('HS_DIAMETERINMM', 'AdditionalAttribute');
export const selectIsOnesided = hasFeatureValueSelectorFactory('root::Heads::Head_2:Type::Luminaire::LightEngine::LE_LightEngineModuleSize', 'None');
export const selectIsBollard = hasFeatureValueSelectorFactory('shared::ProductType::HS_HousingType', 'Bollard');
export const selectIsColumn = hasFeatureValueSelectorFactory('shared::ProductType::HS_HousingType', 'Column');
export const selectHasPathway = hasFeatureValueSelectorFactory('root::Poles::Pole::SpecialLine::HS_Construction', 'AsymmetricPathway');
export const selectHasCustomColor = hasFeatureValueSelectorFactory('root::Heads::Head::ColourCoatingBase::HS_HousingColour', 'Custom');
export const selectLumen = conditionalSelectorFactory('REDTAB.RETURN_LE_LuminousFlux_Max');
export const selectPower = conditionalSelectorFactory('REDTAB.RETURN_EB_Wattage_Max');
export const selectWeight = conditionalSelectorFactory('REDTAB.RETURN_LU_Weight');
export const selectPolar = conditionalSelectorFactory('REDTAB.RETURN_PolarDiagramP2', 'EditorialTable', 1);
export const selectPolar2 = conditionalSelectorFactory('REDTAB.RETURN_PolarDiagramP2', 'EditorialTable', 2);
export const selectLePolar = conditionalSelectorFactory('REDTAB.RETURN_LE_PolarDiagramP2', 'EditorialTable', 1);
export const selectLePolar2 = conditionalSelectorFactory('REDTAB.RETURN_LE_PolarDiagramP2', 'EditorialTable', 2);
export const selectUlor = conditionalSelectorFactory('REDTAB.RETURN_LU_ULOR', 'EditorialTable', 1);
export const selectUlor2 = conditionalSelectorFactory('REDTAB.RETURN_LU_ULOR', 'EditorialTable', 2);
export const selectDatasetnumber = conditionalSelectorFactory('LE_LightEngineDataSetNumber', 'EditorialTable', 1);
export const selectDatasetnumber2 = conditionalSelectorFactory('LE_LightEngineDataSetNumber', 'EditorialTable', 2);
export const selectWindsurfacearea = conditionalSelectorFactory('HS_WindSurfaceArea');
export const selectModulesize = conditionalSelectorFactory('EB_ModuleType');
export const selectZhaga = conditionalSelectorFactory('EB_Zhaga');
export const selectHeight = conditionalSelectorFactory('HS_HousingHeight');
export const selectLLMFUrl = conditionalSelectorFactory('LE_LLMF_JSON');
export const selectCRI = conditionalSelectorFactory('LE_CRI');
export const selectDimensionHeight = conditionalSelectorFactory('REDTAB.RETURN_Dimension_Height');
export const selectDimensionWidth = conditionalSelectorFactory('REDTAB.RETURN_Dimension_Width');
export const selectDimensionDepth = conditionalSelectorFactory('REDTAB.RETURN_Dimension_Depth');
export const selectDimensionLength = conditionalSelectorFactory('REDTAB.RETURN_Dimension_Length');
export const selectDimensionDiameter = conditionalSelectorFactory('REDTAB.RETURN_Dimension_Diameter');


export const selectColorTemperature = createSelector([selectSpcConfiguration], (spcConfiguration) => (
		getValueOfFeature('root::Heads::Head::Type::Luminaire::LightEngine::LE_ColourTemperature', spcConfiguration)
	)
);
export const selectLightDistribution = createSelector([selectSpcConfiguration], (spcConfiguration) => (
		getValueOfFeature('root::Heads::Head::Type::Luminaire::LightEngine::LE_LightDistribution', spcConfiguration)
	)
);
export const selectDimmingLevel = createSelector([selectSpcConfiguration], (spcConfiguration) => (
		getValueOfFeature('root::Heads::Head::Type::Luminaire::LightEngine::LE_DimmingLevel', spcConfiguration)
	)
);
export const selectHeightDecided = createSelector([selectSpcAttributes], (spcAttributes) => (
		isSatisfied('shared::HousingLuminaire::HS_HousingHeight', spcAttributes)
	)
);
export const selectModuleDecided = createSelector([selectSpcAttributes], (spcAttributes) => (
		isSatisfied('root::Heads::Head::Type::Luminaire::LightEngine::LE_LightEngineModuleSize', spcAttributes)
	)
);
export const selectConstructionDecided = createSelector([selectSpcAttributes], (spcAttributes) => (
		isSatisfied('root::Poles::Pole::SpecialLine::HS_Construction', spcAttributes)
	)
);
export const selectHasBuriedBase = createSelector([selectSpcConfiguration], (spcConfiguration) => (
		hasFeatureValue('root::Poles::Pole::Basic::HS_Base', 'BuriedBase', spcConfiguration) ||
		hasFeatureValue('root::Poles::Pole::Basic::HS_Base', 'BuriedBaseShort', spcConfiguration)
	)
);
export const selectHasBase = createSelector([selectSpcConfiguration], (spcConfiguration) => (
		!hasFeatureValue('root::Poles::Pole::Basic::HS_Base', 'None', spcConfiguration)
	)
);
export const selectIsSingle = createSelector([selectSpcConfiguration], (spcConfiguration) => (
		!hasFeatureValue('shared::ProductType::HS_HousingType', 'Column', spcConfiguration) &&
		!hasFeatureValue('shared::ProductType::HS_HousingType', 'Bollard', spcConfiguration) &&
		!hasFeatureValue('shared::ProductType::HS_HousingType', 'ColumnTwist06', spcConfiguration) &&
		!hasFeatureValue('shared::ProductType::HS_HousingType', 'ColumnTwist12', spcConfiguration)
	)
);
export const selectSystemLumen = createSelector([selectSpcConfiguration, selectConditionalVariantValues],
	(spcConfiguration, conditionalVariantValues) => {
		const lumens = conditionals(spcConfiguration, conditionalVariantValues, 'REDTAB.RETURN_LE_LuminousFlux_Max');
		console.log('lumens', lumens);

		return lumens ? lumens.reduce((sum, lumen) => (parseInt(lumen.value, 10) + sum), 0) : undefined;
	}
);
export const selectSystemLumenString = createSelector([selectSpcConfiguration, selectConditionalVariantValues],
	(spcConfiguration, conditionalVariantValues) => {
		const lumens = conditionals(spcConfiguration, conditionalVariantValues, 'REDTAB.RETURN_LE_LuminousFlux_Max');
		return lumens ? lumens.map((lumen) => (parseInt(lumen.value, 10))).join(' + ') : '';
	}
);
export const selectSystemPower = createSelector([selectSpcConfiguration, selectConditionalVariantValues],
	(spcConfiguration, conditionalVariantValues) => {
		const lumens = conditionals(spcConfiguration, conditionalVariantValues, 'REDTAB.RETURN_EB_Wattage_Max');
		return lumens ? lumens.reduce((sum, lumen) => (parseInt(lumen.value, 10) + sum), 0) : undefined;
	}
);
export const selectSystemPowerString = createSelector([selectSpcConfiguration, selectConditionalVariantValues],
	(spcConfiguration, conditionalVariantValues) => {
		const lumens = conditionals(spcConfiguration, conditionalVariantValues, 'REDTAB.RETURN_EB_Wattage_Max');
		return lumens ? lumens.map((lumen) => (parseInt(lumen.value, 10))).join(' + ') : '';
	}
);
export const selectSystemLumenPerPower = createSelector([selectSystemLumen, selectSystemPower],
	(lumen, power) => {
		const lpp = Math.round(lumen / power);
		return !isNaN(lpp) ? lpp : undefined;
	}
);
export const selectModulePos = createSelector([selectSpcConfiguration, selectConditionalVariantValues],
	(spcConfiguration, conditionalVariantValues) => (
		{
			m1top: conditional(spcConfiguration, conditionalVariantValues, 'REDTAB.RETURN_Distance_Module1_From_Top'),
			m1height: conditional(spcConfiguration, conditionalVariantValues, 'REDTAB.RETURN_Total_Height_Module1'),
			m2height: conditional(spcConfiguration, conditionalVariantValues, 'REDTAB.RETURN_Total_Height_Module2'),
			between: conditional(spcConfiguration, conditionalVariantValues, 'REDTAB.RETURN_Distance_Between_Modules')
		}
	)
);
export const selectModulePosPx = createSelector([selectModulePos],
	(modulePos) => (
		{
			m1top: meterToPx(modulePos.m1top),
			m1height: meterToPx(modulePos.m1height),
			m2height: meterToPx(modulePos.m2height),
			between: meterToPx(modulePos.between)
		}
	)
);
export const selectTotalHeightPx = createSelector([selectSpcConfiguration, selectConditionalVariantValues],
	(spcConfiguration, conditionalVariantValues) =>
		meterToPx(conditional(spcConfiguration, conditionalVariantValues, 'REDTAB.RETURN_Total_Height_L')) / 1000
);
export const selectMeasures = createSelector([selectSpcConfiguration, selectConditionalVariantValues, selectIsBollard, selectHasPathway],
	(spcConfiguration, conditionalVariantValues, isBollard, hasPathway) => {
		// fixed heights
		const pathwayHeight = mmToPx('2010'); // 230;
		const columnHeight = 553;
		const baseHeight = 85;

		const l = conditional(spcConfiguration, conditionalVariantValues, 'REDTAB.RETURN_Total_Height_L');
		const l1 = conditional(spcConfiguration, conditionalVariantValues, 'REDTAB.RETURN_Distance_L1');
		const l2 = conditional(spcConfiguration, conditionalVariantValues, 'REDTAB.RETURN_Distance_L2');
		const l3 = conditional(spcConfiguration, conditionalVariantValues, 'REDTAB.RETURN_Distance_L3');
		const l4 = conditional(spcConfiguration, conditionalVariantValues, 'REDTAB.RETURN_Distance_L4');
		const l5 = conditional(spcConfiguration, conditionalVariantValues, 'REDTAB.RETURN_Distance_L5');
		const p1 = conditional(spcConfiguration, conditionalVariantValues, 'REDTAB.RETURN_Distance_P1');
		const p2 = conditional(spcConfiguration, conditionalVariantValues, 'REDTAB.RETURN_Distance_P2');
		const p3 = conditional(spcConfiguration, conditionalVariantValues, 'REDTAB.RETURN_Distance_P3');
		const p4 = conditional(spcConfiguration, conditionalVariantValues, 'REDTAB.RETURN_Distance_P4');
		const u = conditional(spcConfiguration, conditionalVariantValues, 'REDTAB.RETURN_Distance_U');

		// calculate pixel top and heights from top to bottom
		const lTop = 0;
		const lHeight = isBollard ? mmToPx(l) : columnHeight;

		const l1Top = 0;
		const l1Height = mmToPx(l1);

		const l2Top = l1Top + l1Height;
		const l2Height = mmToPx(l2);

		const l3Top = l2Top + l2Height;
		const l3Height = hasPathway ? (columnHeight - pathwayHeight) - l3Top : mmToPx(l3);

		const l4Top = hasPathway ? columnHeight - pathwayHeight : l3Top + l3Height;
		const l4Height = mmToPx(l4);

		// from bottom up
		const p1Height = mmToPx(p1);
		const p2Height = mmToPx(p2);
		const p3Height = mmToPx(p3);
		const p4Height = mmToPx(p4);

		const l5Top = l4Top + l4Height;
		// column has cut
		const l5Height = isBollard ? mmToPx(l5) : (columnHeight - (p1Height + p2Height)) - l5Top;

		const p4Top = isBollard ? l5Top + l5Height : columnHeight - (p1Height + p2Height + p3Height + p4Height);
		const p3Top = isBollard ? l5Top + l5Height : columnHeight - (p1Height + p2Height + p3Height);
		const p2Top = isBollard ? l5Top + l5Height : columnHeight - (p1Height + p2Height);
		const p1Top = isBollard ? p2Top + p2Height : columnHeight - p1Height;

		// base
		const uTop = lHeight;
		const uHeight = mmToPx(u) > 0 ? baseHeight : 0;

		return {
			l: {
				label: 'L',
				mm: l + ' mm',
				top: lTop,
				height: lHeight,
				hidden: false
			},
			l1: {
				label: 'L1',
				mm: l1 + ' mm',
				top: l1Top,
				height: l1Height,
				hidden: l1Height === 0
			},
			l2: {
				label: 'L2',
				mm: l2 + ' mm',
				top: l2Top,
				height: l2Height,
				hidden: l2Height === 0
			},
			l3: {
				label: 'L3',
				mm: l3 + ' mm',
				top: l3Top,
				height: l3Height,
				hidden: l3Height === 0
			},
			l4: {
				label: 'L4',
				mm: l4 + ' mm',
				top: l4Top,
				height: l4Height,
				hidden: l4Height === 0
			},
			l5: {
				label: 'L5',
				mm: l5 + ' mm',
				top: l5Top,
				height: l5Height,
				hidden: true
			},
			// from bottom
			p4: {
				label: 'P4',
				mm: p4 + ' mm',
				top: p4Top,
				height: p4Height,
				hidden: p4Height === 0
			},
			p3: {
				label: 'P3',
				mm: p3 + ' mm',
				top: p3Top,
				height: p3Height,
				hidden: p3Height === 0
			},
			p2: {
				label: 'P2',
				mm: p2 + ' mm',
				top: p2Top,
				height: p2Height,
				hidden: p2Height === 0
			},
			p1: {
				label: 'P1',
				mm: p1 + ' mm',
				top: p1Top,
				height: p1Height,
				hidden: p1Height === 0
			},
			u: {
				label: 'U',
				mm: u + ' mm',
				top: uTop,
				height: uHeight,
				hidden: uHeight === 0
			}
		};
	}
);
export const selectLdts = createSelector([selectSpcConfiguration, selectConditionalVariantValues],
	(spcConfiguration, conditionalVariantValues) => {
		// make sure we are getting ldts in the right order
		const ldt1 = conditional(spcConfiguration, conditionalVariantValues, 'REDTAB.RETURN_LE_LDTFile', 'EditorialTable', 1);
		const ldt2 = conditional(spcConfiguration, conditionalVariantValues, 'REDTAB.RETURN_LE_LDTFile', 'EditorialTable', 2);
		return [
			ldt1 ? {
				url: ldt1,
				name: ldt1.split('/').pop()
			} : null,
			ldt2 ? {
				url: ldt2,
				name: ldt2.split('/').pop()
			} : null
		];
	});
export const selectLdt = createSelector([selectSpcConfiguration, selectConditionalVariantValues],
	(spcConfiguration, conditionalVariantValues) => (
		conditional(spcConfiguration, conditionalVariantValues, 'REDTAB.RETURN_LE_LDTFile', 'EditorialTable', 1)
	));
export const selectLdt1 = createSelector([selectSpcConfiguration, selectConditionalVariantValues],
	(spcConfiguration, conditionalVariantValues) => (
		conditional(spcConfiguration, conditionalVariantValues, 'REDTAB.RETURN_LE_LDTFile', 'EditorialTable', 1)
	));
export const selectLdt2 = createSelector([selectSpcConfiguration, selectConditionalVariantValues],
	(spcConfiguration, conditionalVariantValues) => (
		conditional(spcConfiguration, conditionalVariantValues, 'REDTAB.RETURN_LE_LDTFile', 'EditorialTable', 2)
	));
export const selectFamilyFile = createSelector([selectSpcConfiguration, selectConditionalVariantValues],
	(spcConfiguration, conditionalVariantValues) => (
		conditional(spcConfiguration, conditionalVariantValues, 'REDTAB.RETURN_FamilyFile', 'EditorialTable', 1)
	));
export const selectMountingInstruction = createSelector([selectSpcConfiguration, selectConditionalVariantValues],
	(spcConfiguration, conditionalVariantValues) => (
		conditional(spcConfiguration, conditionalVariantValues, 'REDTAB.RETURN_MountingInstructionLink', 'EditorialTable', 1)
	));
export const selectFeatureModelCompletionPercentage = createSelector([selectSpcAttributes, selectProductData],
	(spcAttributes, productData) => {
		if (spcAttributes) {
			const visibleCount = spcAttributes
				.length ?? 100;
			const satisfiedCount = spcAttributes
				.length ?? 0;
			return Math.round((satisfiedCount / visibleCount * 100));
		} else {
			return 0;
		}
	});
export const selectLightProfilePercentage = createSelector([selectBaseProfileIsSatisfied, selectZoneIsSatisfied, selectLightProfileLocationIsSatisfied, selectLuminaryIsSatisfied, selectFeatureModelCompletionPercentage],
	(base, zone, location, luminary, luminaryPercentage) => (
		(luminary ?
				// if luminary is satisfied, add 40% of luminaryPercentage to the total
				((base ? 20 : 0) + (zone ? 20 : 0) + (location ? 20 : 0) + (luminary ? luminaryPercentage * 0.4 : 0)) :
				// if luminary is not satisfied, ignore luminaryPercentage
				((base ? 33 : 0) + (zone ? 33 : 0) + (location ? 34 : 0))
		)
	)
);
export const selectLightProfileIsSatisfied = createSelector([selectBaseProfileIsSatisfied, selectZoneIsSatisfied, selectLightProfileLocationIsSatisfied, selectLuminaryIsSatisfied, selectSpcIsSatisfied],
	(base, zone, location, luminary, spcSatisfied) => (
		(luminary ?
				(base && zone && location && luminary && spcSatisfied) :
				(base && zone && location)
		)
	)
);
export const selectUnsatisfied = createSelector([selectSectionConfig, selectProductData, selectSpcAttributes],
	(sectionsConfig, productData, spcAttributes) => {
		if (!sectionsConfig || !spcAttributes || !productData || !spcAttributes) return null;
		return sectionsConfig
			.filter(section => !spcAttributes
				.filter(attribute => featureInSection(sectionsConfig, section, attribute.id.localId))
				//.filter(attribute => getProductData(attribute.id.localId, productData)?.isVisible)
				.reduce((acc, attribute) => acc && isSatisfied(attribute.id.localId, spcAttributes), true)
			)
			.map(section => ({
				sectionId: section.id,
				unsatisfiedFeatures: spcAttributes
					.filter(attribute => featureInSection(sectionsConfig, section, attribute.id.localId))
					//.filter(attribute => getProductData(attribute.id.localId, productData)?.isVisible)
					.filter(attribute => !isSatisfied(attribute.id.localId, spcAttributes))
					.map(attribute => attribute.id.localId)
			}));
	});
export const selectVariantCodeTypes = createSelector([selectSpcConfiguration, selectVariantCodeTypeTemplate, selectConditionalVariantValues],
	(spcConfiguration, variantCodeTypeTemplates, conditionalVariantValues) => {
		const variantCodes = [];
		return [];
		/*
		variantCodeTypeTemplates.forEach(variantCodeTypeTemplate => {
			const name = variantCodeTypeTemplate.templateName;
			const definition = variantCodeTypeTemplate.templateDefinition;
			const codeMappings = variantCodeTypeTemplate.codeMappings;
			let result = '' + definition;
			const vars = definition.match(/{([^{}]+)}/g).map(match => match.replace(/[{}]/g, ''));
			vars.forEach(variable => {
				let featureId = variable;
				let replacement = '';
				if (variable.indexOf(':') !== -1) {
					featureId = variable.split(':')[0];
					replacement = variable.split(':')[1];
				}

				// the value of the feature
				const value = getValueOfFeature(featureId, spcConfiguration);
				// the conditional value with the variable name
				const conditionalValue = conditional(spcConfiguration, conditionalVariantValues, variable);
				let code = '';
				if (conditionalValue) {
					// use the conditional, when available
					const mapping = codeMappings.find(codeMap => codeMap.attributeId === featureId && codeMap.attributeValueId === conditionalValue);
					if (mapping) {
						code = replacement === '' ? mapping.code : replacement;
					}

					//console.log('conditionalValue', conditionalValue, code);
				} else {
					// otherwise use the code mapping for the value
					const mapping = codeMappings.find(codeMap => codeMap.attributeId === featureId && codeMap.attributeValueId === value);
					if (mapping) {
						code = replacement === '' ? mapping.code : replacement;
					}
					//console.log('!conditionalValue', mapping, code);
				}
				result = result.replace(`{${featureId}}`, code);
			});
			result = result.replace(/{([^{}]+)}/g, '');
			variantCodes.push({
				id: name,
				code: result
			});
		});
		return variantCodes;
		*/
	});

export const selectMajorNumbers = (state) => state.status.numbers.majorNumbers;
export const selectIntermediateNumbers = (state) => state.status.numbers.intermediateNumbers;

export const selectSystemLuminousFluxMax = createSelector([selectSpcConfiguration, selectConditionalVariantValues],
	(spcConfiguration, conditionalVariantValues) => (
		conditional(spcConfiguration, conditionalVariantValues, 'LU_SystemLuminousFluxMax', 'EditorialTable', 1)
	));
export const selectSystemLuminousFluxMin = createSelector([selectSpcConfiguration, selectConditionalVariantValues],
	(spcConfiguration, conditionalVariantValues) => (
		conditional(spcConfiguration, conditionalVariantValues, 'LU_SystemLuminousFluxMin', 'EditorialTable', 1)
	));
export const selectAvailableDimmingLevel = createSelector([selectSpcConfiguration, selectConditionalVariantValues],
	(spcConfiguration, conditionalVariantValues) => (
		getDimmingLevelSteps(spcConfiguration, conditionalVariantValues)
	));
export const selectStepsForLevel = createSelector([selectSpcConfiguration, selectConditionalVariantValues],
	(spcConfiguration, conditionalVariantValues) => (
		conditional(spcConfiguration, conditionalVariantValues, 'LU_SystemLuminousFluxMin', 'EditorialTable', 1)
	));
export const selectCoefficients = createSelector([selectSpcConfiguration, selectConditionalVariantValues],
	(spcConfiguration, conditionalVariantValues) => {

		const f10 = conditional(spcConfiguration, conditionalVariantValues, 'Formula1_Parameter00', 'EditorialTable', 1);
		const f11 = conditional(spcConfiguration, conditionalVariantValues, 'Formula1_Parameter01', 'EditorialTable', 1);
		const f12 = conditional(spcConfiguration, conditionalVariantValues, 'Formula1_Parameter02', 'EditorialTable', 1);
		const f13 = conditional(spcConfiguration, conditionalVariantValues, 'Formula1_Parameter03', 'EditorialTable', 1);

		const f20 = conditional(spcConfiguration, conditionalVariantValues, 'Formula2_Parameter00', 'EditorialTable', 1);
		const f21 = conditional(spcConfiguration, conditionalVariantValues, 'Formula2_Parameter01', 'EditorialTable', 1);
		const f22 = conditional(spcConfiguration, conditionalVariantValues, 'Formula2_Parameter02', 'EditorialTable', 1);
		const f23 = conditional(spcConfiguration, conditionalVariantValues, 'Formula2_Parameter03', 'EditorialTable', 1);
		// console.log({f10, f11, f12, f13, f20, f21, f22, f23});

		const cs = {
			formular1: {
				type: conditional(spcConfiguration, conditionalVariantValues, 'Formula1_Type', 'EditorialTable', 1),
				coefficients: [
					parseFloat(f10 ? f10.replace(',', '.') : '0'),
					parseFloat(f11 ? f11.replace(',', '.') : '0'),
					parseFloat(f12 ? f12.replace(',', '.') : '0'),
					parseFloat(f13 ? f13.replace(',', '.') : '0'),
				]
			},
			formular2: {
				type: conditional(spcConfiguration, conditionalVariantValues, 'Formula2_Type', 'EditorialTable', 1),
				coefficients: [
					parseFloat(f20 ? f20.replace(',', '.') : '0'),
					parseFloat(f21 ? f21.replace(',', '.') : '0'),
					parseFloat(f22 ? f22.replace(',', '.') : '0'),
					parseFloat(f23 ? f23.replace(',', '.') : '0'),
				]
			}
		};
		return cs;
	});
export const selectHasCoefficients = createSelector([selectCoefficients],
	(coefficients) => !Number.isNaN(coefficients.formular1.coefficients[0]) &&
		coefficients.formular1.coefficients.reduce((acc, c) => acc + c, 0) !== 0 &&
		coefficients.formular2.coefficients.reduce((acc, c) => acc + c, 0) !== 0
);

// specsheet personalisation
export const selectPersonalisation = state => selectConfigPersonalisation(state.configuration);
export const selectHasPersonalisation = state => selectConfigHasPersonalisation(state.configuration);
export const selectShowPersonalisation = state => selectPersonalisation(state)?.active === true;
